import { Badge, Button, Modal, Select } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ERP_ENTITIES,
  type ErpEntity,
  type ErpInboxRow,
  type ErpInboxSource,
  type ErpInboxStatus,
} from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissionCheck } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { erpKeys, fetchErpStatus, fetchInbox, retryInbox } from '../api';
import { EnvironmentBanner } from '../components/EnvironmentBanner';
import { InboxStatusBadge, PayloadBlock } from '../components/status-badges';

const STATUSES: ErpInboxStatus[] = ['pending', 'applied', 'skipped', 'failed'];
const SOURCES: ErpInboxSource[] = ['webhook', 'poll'];

/**
 * The inbox browser: everything Business Central has told us about, and what
 * the WMS did with it. `skipped` rows carry their reason in the error column —
 * that is where "no restaurant for BC customer K00042" surfaces, which is the
 * single most common reason a BC order does not appear in the WMS.
 */
export function InboxPage(): ReactNode {
  const { t } = useTranslation(['bcIntegration', 'common']);
  const can = usePermissionCheck();
  const mayTrigger = can(['integration.sync.trigger']);
  const queryClient = useQueryClient();
  const { state, setState, setFilter } = useListState({ defaultSort: 'receivedAt', defaultDir: 'desc' });
  const [selected, setSelected] = useState<ErpInboxRow | null>(null);

  const status = useQuery({ queryKey: erpKeys.status, queryFn: fetchErpStatus });
  const list = useQuery({ queryKey: erpKeys.inbox(state), queryFn: () => fetchInbox(state) });
  useLiveQuery(['erp'], ['erp']);

  const retry = useMutation({
    mutationFn: retryInbox,
    onSuccess: () => {
      toastSuccess(t('bcIntegration:toast.retryQueued'));
      void queryClient.invalidateQueries({ queryKey: ['erp'] });
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const columns: DataTableColumn<ErpInboxRow>[] = [
    {
      id: 'bcEntity',
      header: t('bcIntegration:inbox.entity'),
      sortField: 'bcEntity',
      cell: (row) => entityLabel(row.bcEntity, t),
    },
    {
      id: 'kind',
      header: t('bcIntegration:inbox.kind'),
      cell: (row) => <span className="font-mono text-xs">{row.kind}</span>,
    },
    {
      id: 'source',
      header: t('bcIntegration:inbox.source'),
      hideBelow: 'sm',
      cell: (row) => <Badge tone="neutral">{t(`bcIntegration:sources.${row.source}`)}</Badge>,
    },
    {
      id: 'status',
      header: t('bcIntegration:inbox.status'),
      sortField: 'status',
      cell: (row) => <InboxStatusBadge status={row.status} />,
    },
    {
      id: 'receivedAt',
      header: t('bcIntegration:inbox.received'),
      sortField: 'receivedAt',
      hideBelow: 'md',
      cell: (row) => formatDateTime(row.receivedAt),
    },
    {
      id: 'error',
      header: t('bcIntegration:inbox.error'),
      cell: (row) => (
        <span
          className={`block max-w-96 truncate text-xs ${row.status === 'failed' ? 'text-red-600' : 'text-ink-muted'}`}
          title={row.error ?? undefined}
        >
          {row.error ?? '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        mayTrigger && row.status !== 'applied' ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={retry.isPending}
            onClick={(event) => {
              event.stopPropagation();
              retry.mutate(row.id);
            }}
          >
            {t('bcIntegration:actions.retry')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader title={t('bcIntegration:inbox.title')} subtitle={t('bcIntegration:inbox.subtitle')} />
      <EnvironmentBanner status={status.data} />

      <DataTable
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={list.data?.total ?? null}
        nextCursor={list.data?.nextCursor ?? null}
        loading={list.isLoading}
        error={list.error}
        searchPlaceholder={t('bcIntegration:inbox.search')}
        emptyTitle={t('bcIntegration:inbox.empty')}
        emptyDescription={t('bcIntegration:inbox.emptyDescription')}
        onRowClick={setSelected}
        filters={
          <>
            <Select
              aria-label={t('bcIntegration:filters.status')}
              value={(state.filters?.status as string | undefined) ?? ''}
              placeholder={t('bcIntegration:filters.status')}
              options={[
                { value: '', label: t('bcIntegration:filters.all') },
                ...STATUSES.map((value) => ({ value, label: t(`bcIntegration:statuses.${value}`) })),
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('bcIntegration:filters.source')}
              value={(state.filters?.source as string | undefined) ?? ''}
              placeholder={t('bcIntegration:filters.source')}
              options={[
                { value: '', label: t('bcIntegration:filters.all') },
                ...SOURCES.map((value) => ({ value, label: t(`bcIntegration:sources.${value}`) })),
              ]}
              onChange={(event) => {
                setFilter('source', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('bcIntegration:filters.entity')}
              value={(state.filters?.bcEntity as string | undefined) ?? ''}
              placeholder={t('bcIntegration:filters.entity')}
              options={[
                { value: '', label: t('bcIntegration:filters.all') },
                ...ERP_ENTITIES.map((value) => ({
                  value,
                  label: t(`bcIntegration:entityNames.${value}`),
                })),
              ]}
              onChange={(event) => {
                setFilter('bcEntity', event.target.value || undefined);
              }}
            />
          </>
        }
      />

      <Modal
        open={selected !== null}
        onClose={() => {
          setSelected(null);
        }}
        title={t('bcIntegration:inbox.detailTitle')}
        subtitle={selected ? `${selected.bcEntity} · ${selected.kind}` : undefined}
        width={720}
      >
        {selected ? (
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Field label={t('bcIntegration:inbox.status')}>
                <InboxStatusBadge status={selected.status} />
              </Field>
              <Field label={t('bcIntegration:inbox.source')}>{t(`bcIntegration:sources.${selected.source}`)}</Field>
              <Field label={t('bcIntegration:inbox.received')}>{formatDateTime(selected.receivedAt)}</Field>
              <Field label={t('bcIntegration:inbox.processed')}>
                {selected.processedAt ? formatDateTime(selected.processedAt) : '—'}
              </Field>
            </dl>
            {selected.error ? (
              <p
                className={`rounded-lg px-3 py-2 text-xs ${
                  selected.status === 'failed' ? 'bg-red-500/10 text-red-700' : 'bg-surface-subtle text-ink-light'
                }`}
              >
                {selected.error}
              </p>
            ) : null}
            <PayloadBlock value={selected.payload} />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function entityLabel(bcEntity: string, t: (key: string) => string): string {
  return (ERP_ENTITIES as readonly string[]).includes(bcEntity)
    ? t(`bcIntegration:entityNames.${bcEntity as ErpEntity}`)
    : bcEntity;
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
